import { useEffect, useRef, useState } from "preact/hooks";

import { AssigneePickerSheet } from "../components/AssigneePickerSheet";
import { useModalA11y } from "../hooks/useModalA11y";
import { createAssignment, splitAssignment } from "../lib/api";
import { clearHandOutDraft, loadHandOutDraft, saveHandOutDraft } from "../lib/handOutDraft";
import { joinNames } from "../lib/names";
import type { DueTask, TeamMemberBoard } from "../types";

export interface HandOutWorkFlowProps {
  roster: TeamMemberBoard[];
  /** The manager's own open Due-today/whenever tasks — the "pick a job"
   * quick list's real-data source (design-spec-today-card.md §3.5 step 1:
   * "pick from a short quick-list or type something new"). Both
   * assignment-kind AND cadence-kind Due-today rows are offered (ADR-008):
   * a recurring cadence chore has no `assignments.id` to split directly
   * (its `id` here is the cadence_id), so a cadence-kind selection is
   * always routed through create-a-new-assignment-then-split, never split
   * in place — see `sendIt` below. */
  myOpenTasks: DueTask[];
  onClose: () => void;
  /** Called once the split has been sent successfully — the caller
   * refreshes both Today (the new/updated parent card) and the Team
   * board. */
  onSent: () => Promise<void> | void;
}

interface PieceDraft {
  localId: string;
  title: string;
  assigneeId: string | null;
}

type Step = 1 | 2 | 3 | 4;

function newLocalId(): string {
  return crypto.randomUUID();
}

/**
 * The hand-out-work overlay (design-spec-today-card.md §3.5): pick/create
 * a job -> split into pieces -> assign each piece via the bottom-sheet
 * picker -> review & send. Cancel (✕) is available at every step and
 * discards the in-progress flow with no save prompt (no partial state is
 * ever persisted server-side until "Send it").
 */
export function HandOutWorkFlow({ roster, myOpenTasks, onClose, onSent }: HandOutWorkFlowProps) {
  // Sprint-006 D5 (F-D4-2) — read any in-progress draft exactly once, on
  // first mount, and seed every piece of draft state from it. `useState`'s
  // lazy-initializer form guarantees `loadHandOutDraft()` runs once per
  // hook (not on every render), and all five reads happen during the same
  // first render, before anything is painted — restoring a backgrounded-
  // tab reload is indistinguishable from a fresh mount that happens to
  // start mid-flow.
  const [step, setStep] = useState<Step>(() => loadHandOutDraft()?.step ?? 1);
  const [selectedExistingId, setSelectedExistingId] = useState<string | null>(
    () => loadHandOutDraft()?.selectedExistingId ?? null,
  );
  const [pickingNew, setPickingNew] = useState(() => loadHandOutDraft()?.pickingNew ?? false);
  const [customTitle, setCustomTitle] = useState(() => loadHandOutDraft()?.customTitle ?? "");
  const [pieces, setPieces] = useState<PieceDraft[]>(() => loadHandOutDraft()?.pieces ?? []);
  const [pickerForPieceId, setPickerForPieceId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState<string | null>(null);
  const createKeyRef = useRef(crypto.randomUUID());
  const splitKeyRef = useRef(crypto.randomUUID());
  const containerRef = useRef<HTMLElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const confirmBackRef = useRef<HTMLButtonElement>(null);

  // Sprint-006 D5 (F-D4-2) — explicit cancel (✕ button, Escape, backdrop —
  // everything routes through `useModalA11y`'s `onClose`) discards the
  // draft outright, matching the flow's existing "no partial state
  // persisted" contract once the person has actually said they're done.
  // An interruption (backgrounded tab) never calls this — only a deliberate
  // close does — so the draft otherwise survives exactly as long as
  // `sessionStorage` does.
  function handleCancel() {
    clearHandOutDraft();
    onClose();
  }

  // Sprint-006 D3 — focus trap + Escape-to-cancel + focus-restore, shared
  // across every step (including the final "Sent!" confirmation screen —
  // this is the same mounted overlay throughout, never a fresh mount per
  // step, so a single `useModalA11y` call covers all of it).
  // Sprint-011 C1 (TD-012, SA plan §3 clause 3) — the ✕ button and the
  // "Sent!" screen's "Back to Today" button both route through the hook's
  // returned `close`, not `handleCancel`/`onClose` directly, so a hardware/
  // browser Back converges on the same shared `popstate` -> `handleCancel`
  // path (which itself still clears the draft before calling `onClose`).
  const { close } = useModalA11y(containerRef, {
    active: true,
    onClose: handleCancel,
    initialFocusRef: cancelButtonRef,
  });

  // The "Sent!" confirmation screen swaps in a whole new `<section>`
  // subtree (see the `confirmText` branch below) — nothing in the previous
  // step's DOM survives, so focus would otherwise silently fall back to
  // `<body>`. Move it deliberately to the one actionable control there.
  useEffect(() => {
    if (confirmText) confirmBackRef.current?.focus();
  }, [confirmText]);

  // Sprint-006 D5 (F-D4-2) — persist the whole draft to `sessionStorage`
  // on every relevant state change, so a backgrounded-tab reload (a real,
  // documented iOS Safari/Android Chrome behavior) restores exactly where
  // the person left off instead of silently discarding minutes of real
  // work (ux-review-sprint-006.md F-D4-2). Skipped once a job has been
  // sent — the confirmation screen (`confirmText` set) has nothing left
  // worth saving, and `sendIt` already clears the draft itself.
  useEffect(() => {
    if (confirmText) return;
    saveHandOutDraft({ step, selectedExistingId, pickingNew, customTitle, pieces });
  }, [step, selectedExistingId, pickingNew, customTitle, pieces, confirmText]);

  const jobTitle = pickingNew
    ? customTitle.trim()
    : (myOpenTasks.find((t) => t.id === selectedExistingId)?.title ?? "");

  const canGoNextFromStep1 = pickingNew
    ? customTitle.trim().length > 0
    : selectedExistingId !== null;
  const canGoNextFromStep2 = pieces.length > 0 && pieces.every((p) => p.title.trim().length > 0);
  const canGoNextFromStep3 = pieces.length > 0 && pieces.every((p) => p.assigneeId !== null);

  function selectExisting(id: string) {
    setSelectedExistingId(id);
    setPickingNew(false);
  }

  function selectNew() {
    setPickingNew(true);
    setSelectedExistingId(null);
  }

  function goToStep2() {
    setPieces([{ localId: newLocalId(), title: "", assigneeId: null }]);
    setStep(2);
  }

  function updatePieceTitle(localId: string, title: string) {
    setPieces((prev) => prev.map((p) => (p.localId === localId ? { ...p, title } : p)));
  }

  function addPiece() {
    setPieces((prev) => [...prev, { localId: newLocalId(), title: "", assigneeId: null }]);
  }

  function removePiece(localId: string) {
    setPieces((prev) => (prev.length > 1 ? prev.filter((p) => p.localId !== localId) : prev));
  }

  function assignPiece(localId: string, assigneeId: string) {
    setPieces((prev) => prev.map((p) => (p.localId === localId ? { ...p, assigneeId } : p)));
    setPickerForPieceId(null);
  }

  async function sendIt() {
    setSending(true);
    setSendError(null);
    try {
      // ADR-008 — a cadence-kind selection must NEVER be passed straight
      // into `splitAssignment`: a cadence occurrence has no
      // `assignments.id` of its own, and its `DueTask.id` here actually
      // holds the cadence_id. Only an assignment-kind selection splits its
      // existing id directly; a cadence-kind selection (and the
      // "Something else…" new-job path) always creates a fresh assignment
      // first and splits that.
      const selected = myOpenTasks.find((t) => t.id === selectedExistingId);
      const splitExisting = !pickingNew && selected?.kind === "assignment";
      let parentId = splitExisting ? selectedExistingId : null;
      if (!parentId) {
        const parent = await createAssignment({
          idempotency_key: createKeyRef.current,
          title: jobTitle,
        });
        parentId = parent.id;
      }
      const result = await splitAssignment(parentId, {
        idempotency_key: splitKeyRef.current,
        pieces: pieces.map((p) => ({
          title: p.title.trim(),
          assignee_id: p.assigneeId ?? undefined,
        })),
      });
      const names = result.created.map((c) => c.assignee_name ?? "someone");
      setConfirmText(
        `Done! ${joinNames(names)} will see their pieces next time they open Hayloft.`,
      );
      // Sprint-006 D5 (F-D4-2) — the draft has now been sent to the
      // server; nothing left to protect against an interruption.
      clearHandOutDraft();
      await onSent();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : "Couldn't send this job — try again.");
    } finally {
      setSending(false);
    }
  }

  const pickerPiece = pieces.find((p) => p.localId === pickerForPieceId) ?? null;

  if (confirmText) {
    return (
      <section
        ref={containerRef}
        class="overlay-view is-open"
        role="dialog"
        aria-modal="true"
        aria-label="Job sent"
      >
        <div class="overlay-header">
          <span style={{ width: "48px" }} />
          <h1 class="step-title">Sent!</h1>
          <span style={{ width: "48px" }} />
        </div>
        <div class="overlay-body" tabIndex={0}>
          <div class="confirm-panel">
            <div class="confirm-check" aria-hidden="true">
              ✓
            </div>
            <p class="confirm-text">{confirmText}</p>
            <button ref={confirmBackRef} type="button" class="secondary-btn" onClick={close}>
              Back to Today
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={containerRef}
      class="overlay-view is-open"
      role="dialog"
      aria-modal="true"
      aria-label="Hand out work"
    >
      <div class="overlay-header">
        <button
          ref={cancelButtonRef}
          type="button"
          class="icon-btn"
          aria-label="Cancel and close"
          onClick={close}
        >
          ✕
        </button>
        <span class="step-title">Step {step} of 4</span>
        <span style={{ width: "48px" }} />
      </div>

      <div class="overlay-body" tabIndex={0}>
        {step === 1 ? (
          <>
            <h1 class="view-heading">What needs doing?</h1>
            <p class="view-subheading">Pick a job, or tell us something new.</p>
            <div class="pick-list">
              {myOpenTasks.map((task) => (
                <button
                  key={task.id}
                  type="button"
                  class={`pick-option${selectedExistingId === task.id ? " is-selected" : ""}`}
                  onClick={() => selectExisting(task.id)}
                >
                  {task.title}
                </button>
              ))}
              <button
                type="button"
                class={`pick-option${pickingNew ? " is-selected" : ""}`}
                onClick={selectNew}
              >
                Something else…
              </button>
            </div>
            {pickingNew ? (
              <input
                type="text"
                class="pick-input"
                placeholder="Type the job"
                aria-label="Type the job"
                value={customTitle}
                onInput={(e) => setCustomTitle((e.target as HTMLInputElement).value)}
              />
            ) : null}
          </>
        ) : null}

        {step === 2 ? (
          <>
            <h1 class="view-heading">Split it into pieces</h1>
            <p class="view-subheading">
              Break the job into pieces you can hand to different people.
            </p>
            {pieces.map((piece, index) => (
              <div key={piece.localId} class="piece-row">
                <input
                  type="text"
                  class="piece-input"
                  placeholder="Describe this piece"
                  aria-label={`Piece ${index + 1}`}
                  value={piece.title}
                  onInput={(e) =>
                    updatePieceTitle(piece.localId, (e.target as HTMLInputElement).value)
                  }
                />
                <button
                  type="button"
                  class="piece-remove"
                  aria-label={
                    piece.title.trim()
                      ? `Remove piece: ${piece.title.trim()}`
                      : `Remove piece ${index + 1}`
                  }
                  onClick={() => removePiece(piece.localId)}
                >
                  ×
                </button>
              </div>
            ))}
            <button type="button" class="add-piece-btn" onClick={addPiece}>
              ＋ Add another piece
            </button>
          </>
        ) : null}

        {step === 3 ? (
          <>
            <h1 class="view-heading">Who's doing what?</h1>
            <p class="view-subheading">Tap a name to pick who does each piece.</p>
            {pieces.map((piece) => {
              const assignee = roster.find((r) => r.user_id === piece.assigneeId);
              return (
                <div key={piece.localId} class="assign-row">
                  <span class="piece-text">{piece.title}</span>
                  <button
                    type="button"
                    class={`assign-chip${assignee ? " is-set" : ""}`}
                    style={
                      assignee
                        ? { background: "var(--pine)", borderColor: "var(--pine)" }
                        : undefined
                    }
                    aria-label={
                      assignee
                        ? `Change who does "${piece.title}" — currently ${assignee.name}`
                        : `Pick someone for "${piece.title}"`
                    }
                    onClick={() => setPickerForPieceId(piece.localId)}
                  >
                    {assignee ? assignee.name : "Pick someone"}
                  </button>
                </div>
              );
            })}
          </>
        ) : null}

        {step === 4 ? (
          <>
            <h1 class="view-heading">Ready to send</h1>
            <p class="view-subheading">{jobTitle}</p>
            {pieces.map((piece) => {
              const assignee = roster.find((r) => r.user_id === piece.assigneeId);
              return (
                <div key={piece.localId} class="summary-row">
                  <span class="dot" style={{ background: "var(--pine)" }} aria-hidden="true" />
                  <b>{assignee?.name ?? "?"}</b>
                  <span>&nbsp;→&nbsp;{piece.title}</span>
                </div>
              );
            })}
            {sendError ? (
              <p class="error-hint" role="alert">
                {sendError}
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      <div class="overlay-footer">
        {step > 1 ? (
          <button
            type="button"
            class="secondary-btn"
            style={{ marginBottom: "10px" }}
            onClick={() => setStep((s) => (s - 1) as Step)}
          >
            Back
          </button>
        ) : null}
        <button
          type="button"
          class="primary-btn"
          disabled={
            sending ||
            (step === 1 && !canGoNextFromStep1) ||
            (step === 2 && !canGoNextFromStep2) ||
            (step === 3 && !canGoNextFromStep3)
          }
          onClick={() => {
            if (step === 1) goToStep2();
            else if (step === 2) setStep(3);
            else if (step === 3) setStep(4);
            else void sendIt();
          }}
        >
          {step === 4 ? (sending ? "Sending…" : "Send it") : "Next"}
        </button>
      </div>

      <AssigneePickerSheet
        open={pickerPiece !== null}
        pieceTitle={pickerPiece?.title ?? ""}
        options={roster.map((r) => ({ id: r.user_id, name: r.name }))}
        selectedId={pickerPiece?.assigneeId ?? null}
        onSelect={(id) => pickerPiece && assignPiece(pickerPiece.localId, id)}
        onCancel={() => setPickerForPieceId(null)}
      />
    </section>
  );
}
